import { gql, useQuery } from '@apollo/client';
import { Table, TableBody, TableCell, TableContainer, TableRow, type Theme } from '@mui/material';
import { useState } from 'react';
import { ComponentBox } from '@/admins/SmartReports/components/Components/Common/ComponentBox';
import { ComponentTitle } from '@/admins/SmartReports/components/Components/Common/ComponentTitle';
import { LoadingErrorWrapper } from '@/admins/SmartReports/components/Components/Common/LoadingErrorWrapper';
import type { Asset } from '@/types/GraphqlSchema';
import { SmartReportComponentTypesEnum, SmartReportTypeEnum } from '../../../types';
import type {
  ComponentDefinition,
  ComponentEditProps,
  ComponentViewProps,
  UseDataType,
} from '../types';

export const COMPONENT_ID = 'reservoir_characteristics_table';

const styles = {
  characteristic: {
    fontFamily: (theme: Theme) => theme.fonts.inter,
    fontWeight: 'bold',
    border: 'none',
    backgroundColor: 'background.paper',
    fontSize: 'clamp(6px, 1.4vw, 12px)',
    padding: 'clamp(1px, 0.3vw, 8px)',
    lineHeight: 1,
  },
  value: {
    fontFamily: (theme: Theme) => theme.fonts.inter,
    border: 'none',
    backgroundColor: 'background.paper',
    fontSize: 'clamp(6px, 1.4vw, 12px)',
    padding: 'clamp(1px, 0.3vw, 8px)',
    lineHeight: 1,
    textAlign: 'right',
  },
};

// Processed data type for display
type ReservoirCharacteristics = {
  characteristic: string;
  value: string;
};

// Process asset data into characteristic-value pairs
const processAssetData = (asset: Asset): ReservoirCharacteristics[] => {
  const characteristics: ReservoirCharacteristics[] = [];

  // Get primary geology (first primary formation or first geology)
  const primaryGeology = asset.geologies?.find(geo => geo.primaryFormation) || asset.geologies?.[0];

  // Period
  if (primaryGeology?.period?.name) {
    characteristics.push({
      characteristic: 'Period',
      value: primaryGeology.period.name,
    });
  }

  // Epoch
  if (primaryGeology?.epoch?.name) {
    characteristics.push({
      characteristic: 'Epoch',
      value: primaryGeology.epoch.name,
    });
  }

  // Age
  if (primaryGeology?.age?.name) {
    characteristics.push({
      characteristic: 'Age',
      value: primaryGeology.age.name,
    });
  } else {
    characteristics.push({
      characteristic: 'Age',
      value: 'N/A',
    });
  }

  // Formation
  if (primaryGeology?.formation?.name) {
    characteristics.push({
      characteristic: 'Formation',
      value: primaryGeology.formation.name,
    });
  }

  // Lithology
  if (primaryGeology?.lithologies?.length) {
    const lithologyNames = primaryGeology.lithologies.map(lith => lith.name).filter(Boolean);
    if (lithologyNames.length > 0) {
      characteristics.push({
        characteristic: 'Lithology',
        value: lithologyNames.join(', '),
      });
    }
  }

  // API
  if (asset.api) {
    characteristics.push({
      characteristic: 'API',
      value: Math.round(asset.api).toString(),
    });
  }

  // Reservoir Depth (from geology first, then fallback to asset)
  if (primaryGeology?.avgReservoirDepthM) {
    characteristics.push({
      characteristic: 'Reservoir Depth (m)',
      value: Math.round(primaryGeology.avgReservoirDepthM).toString(),
    });
  } else if (asset.reservoirDepthM) {
    characteristics.push({
      characteristic: 'Reservoir Depth (m)',
      value: Math.round(asset.reservoirDepthM).toString(),
    });
  }

  return characteristics;
};

// GraphQL query
const QUERY = gql`
  query AssetV2($input: EntityIdentifierInput!) {
    assetV2(input: $input) {
      id
      api
      reservoirDepthM
      geologies {
        id
        primaryFormation
        avgReservoirDepthM
        period {
          name
        }
        epoch {
          name
        }
        age {
          name
        }
        formation {
          name
        }
        lithologies {
          id
          name
        }
      }
    }
  }
`;

const useData = ({ portfolio, persistedData, onComponentLoad }: UseDataType<Asset | null>) => {
  const [dataFetched, setDataFetched] = useState(persistedData);

  // Extract asset_id from portfolio
  const assetId = portfolio?.assets?.[0];

  const { loading, error } = useQuery<{ assetV2: Asset | null }>(QUERY, {
    variables: {
      input: {
        id: assetId,
      },
    },
    skip: !assetId || !!dataFetched,
    fetchPolicy: 'network-only', // Force network request to get fresh data
    onCompleted: response => {
      if (response?.assetV2) {
        setDataFetched(response.assetV2);
        if (onComponentLoad) {
          onComponentLoad({
            componentId: COMPONENT_ID,
            data: response.assetV2,
          });
        }
      }
    },
  });

  return {
    data: dataFetched,
    error,
    loading,
  };
};

const ComponentView = ({ data }: ComponentViewProps<Asset | null | undefined>) => {
  const displayData = data ? processAssetData(data) : [];

  if (displayData.length === 0) return null;

  return (
    // Responsive font sizing based on container size
    <ComponentBox sx={{ fontSize: 'clamp(8px, 2vw, 14px)' }}>
      <ComponentTitle>Reservoir / Hydrocarbon Characteristics</ComponentTitle>
      <TableContainer>
        <Table size='small'>
          <TableBody>
            {displayData.map(item => (
              <TableRow key={item.characteristic}>
                <TableCell sx={styles.characteristic}>{item.characteristic}</TableCell>
                <TableCell sx={styles.value}>{item.value}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </ComponentBox>
  );
};

const ComponentEdit = ({
  portfolio,
  persistedData,
  onComponentLoad,
}: ComponentEditProps<Asset | null>) => {
  const { data, loading, error } = useData({
    portfolio,
    persistedData,
    onComponentLoad,
  });

  return (
    <LoadingErrorWrapper loading={loading} error={error} data={data}>
      <ComponentView data={data} />
    </LoadingErrorWrapper>
  );
};

export const ReservoirCharacteristicsTableComponent: ComponentDefinition<Asset | null> = {
  id: COMPONENT_ID,
  name: 'Reservoir Characteristics',
  type: SmartReportComponentTypesEnum.Table,
  reportTypes: [SmartReportTypeEnum.Asset],
  Component: ComponentEdit,
  ComponentView,
};
